/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./Review.css";
import { simpleFetch } from "../../restWrapper";
import { renderErrorMsg } from "../../assets/component/renderErrorMsg.js";
import PropTypes from "prop-types";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { isMobile } from "react-device-detect";
import { NumericFormat } from "react-number-format";
import { Helmet } from "react-helmet";
import { QRCodeCanvas } from "qrcode.react";
import { isObjectEmpty, resolveThemeColors, withoutExpressPaymentAuthorisation, formatWalletPaymentMethod } from "../../assets/helper.js";
import { getProjectTitle } from "../../assets/projectTitleHelper.js";
import hkRegions, { getDistrictsByRegion } from "../../assets/hkAddressData.js";
import checkplusLogo from "../../assets/component/images/checkplus-logo.svg";
import plugLogo from "../../assets/component/images/plug-logo.svg";
import { PLATFORM } from "../../config";

// PaymentIntent statuses on the way to a charge once the 3DS challenge is
// done: the page stays behind the loader through them (CP-408)
const SETTLING_STATUSES = ["processing", "requires_confirmation", "requires_capture", "succeeded"];
const isSettling = (status) => SETTLING_STATUSES.includes(status);

class Review extends Component {
  constructor(props) {
    super(props);
    try {
      if (this.props.location.state === null) {
        this.props.history.push("/SelectMission");
      }
      // Ensure organization exists in state, even if empty
      const locationState = this.props.location.state || {};
      const organization = locationState.organization || {};

      const initialFeeBreakdown =
        locationState.feeBreakdown && typeof locationState.feeBreakdown === "object"
          ? locationState.feeBreakdown
          : null;
      const hasInitialFeeBreakdown = !!initialFeeBreakdown;

      
      this.state = {
        ...locationState,
        ...{
          organization, // Ensure organization is always defined
          language: this.props.language,
          errMsg: false,
          errMsgDetails: "",
          loading: false,
          buttonClick: "false",
          fileUploaded: locationState.fileUploaded || false,
          uploadedFileName: locationState.uploadedFileName || "",
          file: locationState.file || null,
          paymentReference: locationState.paymentReference || "",
          feeBreakdown: initialFeeBreakdown,
          feeCalculationError: null,
          isFeeLoading: !hasInitialFeeBreakdown,
          hasFetchedFeeBreakdown: hasInitialFeeBreakdown,
        },
      };
    } catch (e) {
      this.props.history.push("/SelectMission");
    }
    this.onBackBtnClicked = this.onBackBtnClicked.bind(this);
    this.onChangePaymentMethodClicked = this.onChangePaymentMethodClicked.bind(this);
    this.buildSelectPaymentState = this.buildSelectPaymentState.bind(this);
    this.onNextBtnClicked = this.onNextBtnClicked.bind(this);
    this.handleFileUpload = this.handleFileUpload.bind(this);
    this.handleRemoveFile = this.handleRemoveFile.bind(this);
    this.onScroll = this.onScroll.bind(this);

    this.isComponentMounted = false;
    this.latestFeeRequestId = 0;
  }

  componentDidMount() {
    this.isComponentMounted = true;
    window.scrollTo(0, 0);
    this.setState({
      isScrolling: false,
    });

    window.addEventListener("scroll", this.onScroll);

    // Add logging to debug organization object
    // console.log("Review component mounted with organization:", this.state.organization);
    // console.log("Organization ID:", this.state.organization.organizationId);
    // console.log(JSON.stringify(this.state) + " -45");

    // Debug coverFee state
    // console.log("Cover Fee state on mount:", this.state.coverFee);
    // console.log("Amount state on mount:", this.state.amount);

    // Debug date fields
    // console.log("=== REVIEW DATE DEBUG ===");
    // console.log("scheduledDate:", this.state.scheduledDate);
    // console.log("startDate:", this.state.startDate);
    // console.log("frequency:", this.state.frequency);
    // console.log("isRecurring:", this.state.isRecurring);
    // console.log("recurringOccurrences:", this.state.recurringOccurrences);
    // console.log("isInfiniteRecurring:", this.state.isInfiniteRecurring);
    // console.log("=== END DATE DEBUG ===");
    
    // Ensure IP-based country is set, default to HK if not provided
    // NOTE: This is the user's LOCATION (IP-based), NOT the card issuer country
    // Card issuer country affects Stripe fees (HK: 2.9% vs Overseas: 3.4%)
    if (!this.state.country) {
      this.setState({ country: "HK" });
    }

    // Fetch fee breakdown when amount is available
    const amountValue = Number(this.state.amount);
    if (amountValue > 0) {
      this.fetchFeeBreakdown();
    } else {
      this.setState({
        isFeeLoading: false,
        hasFetchedFeeBreakdown: true,
      });
    }

  }

  componentWillUnmount() {
    this.isComponentMounted = false;
    window.removeEventListener("scroll", this.onScroll);

    if (this.retrievePaymentStatus) {
      clearInterval(this.retrievePaymentStatus);
      this.retrievePaymentStatus = null;
    }
    document.body.style.overflow = "";
  }

  componentDidUpdate(prevProps, prevState) {
    // Freeze the page behind the 3DS overlay; otherwise it still scrolls
    // and shows through the backdrop
    const is3DS = this.state.status === "requires_action";
    if (is3DS !== (prevState.status === "requires_action")) {
      document.body.style.overflow = is3DS ? "hidden" : "";
    }

    // Fetch fee breakdown when relevant fields change
    if (
      (this.state.coverFee !== prevState.coverFee ||
       this.state.amount !== prevState.amount ||
       this.state.brand !== prevState.brand ||
       this.state.cardCountry !== prevState.cardCountry) &&
      Number(this.state.amount) > 0
    ) {
      this.fetchFeeBreakdown();
    }
  }

  getIsFreePlanFlow() {
    const { isFreePlanFlow, organization } = this.state;
    // If explicitly set, use that value
    if (typeof isFreePlanFlow === 'boolean') {
      return isFreePlanFlow;
    }
    // Otherwise, check organization's current plan
    if (organization && organization.currentPlan === 'free') {
      return true;
    }
    return false;
  }

  onScroll() {
    const shouldBeScrolling = window.scrollY >= 100;
    // Only update state if the value actually changed to prevent unnecessary re-renders
    if (shouldBeScrolling !== this.state.isScrolling) {
      this.setState({
        isScrolling: shouldBeScrolling,
      });
    }
  }

  onBackBtnClicked() {
    this.props.history.push({
      pathname: "/SelectPayment",
      state: this.buildSelectPaymentState(),
    });
  }

  // Explicitly changing payment method must drop any express-payment approval:
  // an Apple Pay / Google Pay authorisation is tied to the method and amount the
  // donor saw in the wallet sheet (CP-253). paymentMethod is cleared here too -
  // unlike on MissionDetails, where the key doubles as the organisation's config,
  // on this screen it always holds the donor's own payment method.
  onChangePaymentMethodClicked() {
    const {
      paymentMethod: _discardedPaymentMethod,
      ...state
    } = withoutExpressPaymentAuthorisation(this.buildSelectPaymentState());

    this.props.history.push({ pathname: "/SelectPayment", state });
  }

  // Shared by the Back control and the payment-method Change link: carries the
  // donor's edits (amount, cover-fee choice, details, custom fields) back to the
  // payment-method step instead of losing them.
  buildSelectPaymentState() {
    const prevState = this.props.location.state || {};

    const obj = {
      ...prevState,
      amount:
        this.state.amount !== undefined && this.state.amount !== null
          ? this.state.amount
          : prevState.amount,
      coverFee:
        typeof this.state.coverFee === "boolean"
          ? this.state.coverFee
          : prevState.coverFee,
      feeBreakdown:
        this.state.feeBreakdown !== undefined && this.state.feeBreakdown !== null
          ? this.state.feeBreakdown
          : prevState.feeBreakdown,
      paymentMethod: this.state.paymentMethod || prevState.paymentMethod,
      country: this.state.country || prevState.country,
      cardCountry: this.state.cardCountry || prevState.cardCountry,
      transactionType: this.state.transactionType || prevState.transactionType,
      scheduledDate: this.state.scheduledDate || prevState.scheduledDate,
      recurringOccurrences:
        this.state.recurringOccurrences || prevState.recurringOccurrences,
    };

    if (this.state.donorInfo) obj.donorInfo = this.state.donorInfo;
    if (this.state.customFields) obj.customFields = this.state.customFields;

    // Preserve authentication state when going back
    if (this.props.isAuth || prevState.isAuth || prevState.userAuthenticated) {
      obj.isAuth = true;
      obj.userAuthenticated = true;
    }

    return obj;
  }

  handleFileUpload(e) {
    const file = e.target.files[0];
    if (file) {
      // Handle file upload logic here
      this.setState({ fileUploaded: true, uploadedFileName: file.name, file }); // Add this line
    }
  }

  handleRemoveFile() {
    this.setState({ fileUploaded: false, uploadedFileName: "" });
  }

  handleAddManualPayment() {
    const { buttonClick, fileUploaded, donorInfo, customFields } = this.state;

    if (buttonClick && !fileUploaded) {
      return; // Prevent action if button is disabled
    }
    
    this.setState({
      loading: true,
    });

    let addPaymentPath = '/donors/payments/add';

    const formData = new FormData();
    
    // Use project ID from donations array if available, otherwise use mission ID
    const projectId = this.state.donations && this.state.donations.length > 0 && this.state.donations[0].mission 
      ? this.state.donations[0].mission.id 
      : this.state.mission.id;

    if (projectId === "") {
      // If project ID is empty, add projectName based on transaction type
      const projectName = this.state.transactionType === 'donation' ? 'General Donation' : 'General Payment';


      formData.append('projectName', projectName);
    } else {

      formData.append('projectId', projectId);
    }
    
    // Only append organizationId if it exists
    if (this.state.organization && this.state.organization.organizationId) {
      formData.append('organizationId', this.state.organization.organizationId);
    } else {
      // console.warn("organizationId is missing in manual payment");
    }
    
    // Only append organizationViewId if it exists
    if (this.state.organization && this.state.organization.organizationViewId) {
      formData.append('organizationViewId', this.state.organization.organizationViewId);
    } else {
      // console.warn("organizationViewId is missing in manual payment");
    }
    
    formData.append('amount', this.state.amount);
    formData.append('currency', 'hkd');
    formData.append('language', this.state.language);
    formData.append('country', this.state.country);
    formData.append('memo', donorInfo.memo || '');
    formData.append('scheduledDate', this.state.scheduledDate);
    formData.append('proofofpayment', this.state.file, this.state.uploadedFileName);
    formData.append('paymentName', this.state.brand);
    formData.append('transactionType', this.state.transactionType || 'donation');
    if (this.state.paymentReference) {
      formData.append('paymentReference', this.state.paymentReference);
    }

    if (!this.props.isAuth) {
      addPaymentPath = '/user/payments/add';

      // Add donor info to the form data for public
      formData.append('firstName', donorInfo.firstName);
      formData.append('lastName', donorInfo.lastName);
      formData.append('email', donorInfo.email);
      formData.append('phoneNumber', donorInfo.phoneNumber);
    }

    if (customFields && !isObjectEmpty(customFields)) {
      Object.values(customFields).forEach((field, idx) => {
        formData.append(`fields[${idx}][id]`, field.id);
        formData.append(`fields[${idx}][value]`, field.value);
      });
    }


    simpleFetch(addPaymentPath, 'POST', formData, true).then(res => {
      
      // Check if the response has an id (successful payment creation)
      if (res.body && res.body.id) {

        // Navigate to ConfirmPayment without setting loading to false
        this.props.history.push({
          pathname: "/ConfirmPayment",
          state: {
            organization: this.state.organization,
            projectReturnUrl: this.state.mission.projectReturnUrl,
            brand: this.state.brand, // Pass the brand information
            isRecurring: undefined, // Manual payments don't support recurring
            scheduledDate: undefined,
          },
        });
      } else if (res.body && res.body.message) {
        // If there's an error message in the response

        this.setState({
          errMsgDetails: res.body.message,
          errMsg: true,
          loading: false, // Set loading to false on error
        });
      } else {
        // Generic error

        this.setState({
          errMsgDetails: "Error creating payment",
          errMsg: true,
          loading: false, // Set loading to false on error
        });
      }
    }).catch(error => {
      this.setState({
        errMsgDetails: "Network error while creating payment",
        errMsg: true,
        loading: false,
      });
    });
  }

  calculateRecurringEndDate() {
    // Calculate end date based on frequency and occurrences
    // If user selects 3 occurrences, the subscription runs 3 times:
    // Payment 1: startDate, Payment 2: startDate + 1 interval, Payment 3: startDate + 2 intervals
    // So end date should be startDate + (occurrences - 1) intervals
    if (!this.state.startDate || !this.state.recurringOccurrences || this.state.recurringOccurrences <= 0) {
      return null;
    }

    const startDate = new Date(this.state.startDate);
    const occurrences = this.state.recurringOccurrences;

    let endDate = new Date(startDate);

    switch (this.state.frequency) {
      case "DAILY":
        endDate.setDate(endDate.getDate() + (occurrences - 1));
        break;
      case "WEEKLY":
        endDate.setDate(endDate.getDate() + ((occurrences - 1) * 7));
        break;
      case "BIWEEKLY":
        endDate.setDate(endDate.getDate() + ((occurrences - 1) * 14));
        break;
      case "MONTHLY":
        endDate.setMonth(endDate.getMonth() + (occurrences - 1));
        break;
      case "QUARTERLY":
        endDate.setMonth(endDate.getMonth() + ((occurrences - 1) * 3));
        break;
      case "YEARLY":
        endDate.setFullYear(endDate.getFullYear() + (occurrences - 1));
        break;
      default:
        return null;
    }

    return endDate.toISOString();
  }

  onNextBtnClicked(e) {
    e.preventDefault();
    this.setState({
      buttonClick: true,
    });

    if (this.state.loading || this.state.status === "requires_action") return false;

    // An earlier attempt was given up on but may still go through; settle
    // it before creating a second PaymentIntent, which could charge twice
    if (this.state.staleIntent) {
      this.resumeStaleIntent();
      return false;
    }

    // Validate amount before proceeding
    const amount = Number(this.state.amount);
    if (!amount || amount <= 0) {
      this.setState({
        errMsg: true,
        errMsgDetails: "Invalid payment amount. Amount must be greater than zero.",
        loading: false
      });
      return false;
    }

    this.setState({
      loading: true,
    });

    // eslint-disable-next-line no-undef
    const manualPaymentMethods = new Set([
      "alipay",
      "alipay_hk",
      "alipay_ch",
      "wechatpay",
      "wechatpay_hk",
      "wechatpay_ch",
      "bocpay",
      "octopus",
      "fps",
      "bank_transfer",
      "check_writing",
      "cash",
      "paypal_manual",
    ]);

    if (manualPaymentMethods.has(this.state.brand) && !this.state.fileUploaded) {
      // Show an error message and reset loading state
      this.setState({ 
        errMsg: true,
        errMsgDetails: "Please upload a supporting document before proceeding.",
        loading: false
      });
      return;
    }

    const languageObj = this.props.languageObj;

    // Validate that PayMe/TapAndGo/PayPal are not used for recurring payments
    const isRecurringPayment = (this.state.frequency === "WEEKLY" || this.state.frequency === "BIWEEKLY" || this.state.frequency === "MONTHLY" || this.state.frequency === "QUARTERLY") && (this.state.isInfiniteRecurring || this.state.recurringOccurrences > 0);
    
    if (this.state.brand === "PayMe") {
      if (isRecurringPayment) {
        this.setState({
          errMsg: true,
          errMsgDetails: "PayMe does not support recurring payments. Please use a credit/debit card for recurring donations.",
          loading: false
        });
        return;
      }
      // Carry the donor's live fee-coverage choice (it can be toggled on this
      // screen, so location.state may be stale). The uplift itself is applied
      // by the backend when it issues the PayMe payment request, using the
      // org's negotiated rate - the QR code cannot be re-priced later (CP-281).
      const dataObj = {
        ...this.props.location.state,
        coverFee: this.state.coverFee || false,
      };
      this.props.history.push({
        pathname: isMobile ? "/PayMeLink" : "/PayMeCode",
        state: dataObj,
      });
      return;
    }
    
    if (this.state.brand === "TapAndGo") {
      if (isRecurringPayment) {
        this.setState({
          errMsg: true,
          errMsgDetails: "Tap & Go does not support recurring payments. Please use a credit/debit card for recurring donations.",
          loading: false
        });
        return;
      }
      const dataObj = this.props.location.state;
      this.props.history.push({
        pathname: isMobile ? "/TapAndGoLink" : "/TapAndGoCode",
        state: dataObj,
      });
      return;
    }

    if (this.state.brand === "paypal") {
      if (isRecurringPayment) {
        this.setState({
          errMsg: true,
          errMsgDetails: "PayPal does not support recurring payments. Please use a credit/debit card for recurring donations.",
          loading: false
        });
        return;
      }
      const dataObj = this.props.location.state;
      this.props.history.push({
        pathname: "/PayPal",
        state: dataObj,
      });
      return;
    }

    if (manualPaymentMethods.has(this.state.brand)) {
      // Validate that manual payment methods are not used for recurring payments
      const isRecurring = (this.state.frequency === "WEEKLY" || this.state.frequency === "BIWEEKLY" || this.state.frequency === "MONTHLY" || this.state.frequency === "QUARTERLY") && (this.state.isInfiniteRecurring || this.state.recurringOccurrences > 0);
      if (isRecurring) {
        this.setState({
          errMsg: true,
          errMsgDetails: "Manual payment methods do not support recurring payments. Please use a credit/debit card for recurring donations.",
          loading: false
        });
        return;
      }
      // handle manual payment methods
      this.handleAddManualPayment();

      return; // Add return to prevent executing the rest of the function
    } else {
      let lastName;
      if (this.state.wallet) {
        if (!this.state.donorInfo) {
          lastName = this.state.lastName;
        } else {
          lastName = this.state.donorInfo && this.state.donorInfo.lastName;
        }
      } else {
        lastName = this.state.donorInfo && this.state.donorInfo.lastName;
      }

      let firstName;

      if (this.state.wallet) {
        if (!this.state.donorInfo) {
          firstName = this.state.firstName;
        } else {
          firstName = this.state.donorInfo && this.state.donorInfo.firstName;
        }
      } else {
        firstName = this.state.donorInfo && this.state.donorInfo.firstName;
      }


      let email;

      if (this.state.wallet) {
        if (!this.state.donorInfo) {
          email = this.state.email;
        } else {
          email = this.state.donorInfo && this.state.donorInfo.email;
        }
      } else {
        email = this.state.donorInfo && this.state.donorInfo.email;
      }

      let phoneNumber;

      if (this.state.wallet) {
        if (!this.state.donorInfo) {
          phoneNumber = this.state.phoneNumber;
        } else {
          phoneNumber = this.state.donorInfo && this.state.donorInfo.phoneNumber;
        }
      } else {
        phoneNumber = this.state.donorInfo && this.state.donorInfo.phoneNumber;
      }

      const { donorInfo } = this.state;


      const { memo } = donorInfo || { memo: "" };
      const { orgName } = donorInfo || { orgName: "" };

      let paymentInfo;

      if (this.props.isAuth) {
        // Use project ID from donations array if available, otherwise use mission ID
        const projectId = this.state.donations && this.state.donations.length > 0 && this.state.donations[0].mission 
          ? this.state.donations[0].mission.id 
          : this.state.mission.id;
        
        // console.log("Using project ID for authenticated payment:", projectId);
        
        // Backend calculates fees - just send original amount and coverFee flag
        paymentInfo = {
          projectId: projectId,
          amount: this.state.amount, // Always send original amount, backend handles fees
          currency: "hkd",
          memo: memo,
          country: this.state.country,
          /* Stripe specific */
          paymentMethod: this.state.paymentMethod,
          type: "card",
          scheduledDate: this.state.scheduledDate,
          transactionType: this.state.transactionType || 'donation',
          coverFee: this.state.coverFee || false, // Backend uses this to add fees
          baseAmount: this.state.amount, // Store base amount before fees for editing
          feeBreakdown: this.state.feeBreakdown || null, // Include fee breakdown from state
          /* Recurring payment fields */
          isRecurring: (this.state.frequency === "WEEKLY" || this.state.frequency === "BIWEEKLY" || this.state.frequency === "MONTHLY" || this.state.frequency === "QUARTERLY") && (this.state.isInfiniteRecurring || this.state.recurringOccurrences > 0),
          recurringInterval: this.state.frequency === "WEEKLY" ? "weekly" : this.state.frequency === "BIWEEKLY" ? "biweekly" : this.state.frequency === "MONTHLY" ? "monthly" : this.state.frequency === "QUARTERLY" ? "quarterly" : undefined,
          recurringDay: (this.state.frequency === "MONTHLY" || this.state.frequency === "QUARTERLY") && this.state.startDate ? new Date(this.state.startDate).getDate() : undefined,
          recurringEndDate: (this.state.frequency === "WEEKLY" || this.state.frequency === "BIWEEKLY" || this.state.frequency === "MONTHLY" || this.state.frequency === "QUARTERLY") ? (this.state.isInfiniteRecurring ? null : this.calculateRecurringEndDate()) : undefined
        };

        // Debug logging for recurring payment creation
        // console.log("=== RECURRING PAYMENT DEBUG (Authenticated) ===");
        // console.log("frequency:", this.state.frequency);
        // console.log("isInfiniteRecurring:", this.state.isInfiniteRecurring);
        // console.log("recurringOccurrences:", this.state.recurringOccurrences);
        // console.log("Calculated isRecurring:", paymentInfo.isRecurring);
        // console.log("Calculated recurringInterval:", paymentInfo.recurringInterval);
        // console.log("Calculated recurringDay:", paymentInfo.recurringDay);
        // console.log("Calculated recurringEndDate:", paymentInfo.recurringEndDate);
        // console.log("=== END DEBUG ===");

        // Only include fingerprint if it exists
        if (this.state.fingerprint) {
          paymentInfo.fingerprint = this.state.fingerprint;
        }

        // Only include projectId if it's a valid numeric value
        // For general donations/payments, omit projectId and use projectName + organizationId
        if (projectId && projectId !== '' && projectId !== 'no-project' && !isNaN(projectId)) {
          paymentInfo.projectId = projectId;
          // console.log("Using numeric projectId for authenticated payment:", projectId);
        } else {
          // For general donations/payments, don't include projectId at all
          // Use the same transactionType as in paymentInfo to ensure consistency
          const actualTransactionType = this.state.transactionType || 'donation';
          const projectName = actualTransactionType === 'donation' ? 'General Donation' : 'General Payment';
          // console.log("Omitting projectId, using projectName:", projectName, "for transactionType:", actualTransactionType);
          paymentInfo.projectName = projectName;
        }
        
        // Only include organizationId if it exists
        if (this.state.organization && this.state.organization.organizationId) {
          paymentInfo.organizationId = this.state.organization.organizationId;
        } else {
          // console.warn("organizationId is missing in authenticated payment");
        }

        if (this.state.wallet) {
          /* Stripe specific */
          paymentInfo.wallet = this.state.wallet;
        }
        // console.log(JSON.stringify(paymentInfo) + " -281");
      } else if (this.state.brand !== "PayMe" && this.state.brand !== "fps") {
        // console.log(this.state.brand + " -283");
        // console.log(lastName + firstName + email + phoneNumber + " -307");
        
        // Use project ID from donations array if available, otherwise use mission ID
        const projectId = this.state.donations && this.state.donations.length > 0 && this.state.donations[0].mission 
          ? this.state.donations[0].mission.id 
          : this.state.mission.id;
        
        
        // Backend calculates fees - just send original amount and coverFee flag
        paymentInfo = {
          projectId: projectId,
          lastName: lastName,
          firstName: firstName,
          email: email,
          phoneNumber: phoneNumber,
          amount: this.state.amount, // Always send original amount, backend handles fees
          currency: "hkd",
          memo: memo,
          type: "card",
          country: this.state.country,
          /* Stripe specific */
          paymentMethod: this.state.paymentMethod,
          fingerprint: this.state.fingerprint,
          transactionType: this.state.transactionType || 'donation',
          coverFee: this.state.coverFee || false, // Backend uses this to add fees
          baseAmount: this.state.amount, // Store base amount before fees for editing
          feeBreakdown: this.state.feeBreakdown || null, // Include fee breakdown from state
          /* Recurring payment fields */
          isRecurring: (this.state.frequency === "WEEKLY" || this.state.frequency === "BIWEEKLY" || this.state.frequency === "MONTHLY" || this.state.frequency === "QUARTERLY") && (this.state.isInfiniteRecurring || this.state.recurringOccurrences > 0),
          recurringInterval: this.state.frequency === "WEEKLY" ? "weekly" : this.state.frequency === "BIWEEKLY" ? "biweekly" : this.state.frequency === "MONTHLY" ? "monthly" : this.state.frequency === "QUARTERLY" ? "quarterly" : undefined,
          recurringDay: (this.state.frequency === "MONTHLY" || this.state.frequency === "QUARTERLY") && this.state.startDate ? new Date(this.state.startDate).getDate() : undefined,
          recurringEndDate: (this.state.frequency === "WEEKLY" || this.state.frequency === "BIWEEKLY" || this.state.frequency === "MONTHLY" || this.state.frequency === "QUARTERLY") ? (this.state.isInfiniteRecurring ? null : this.calculateRecurringEndDate()) : undefined
        };

        // Debug logging for recurring payment creation
        // console.log("=== RECURRING PAYMENT DEBUG ===");
        // console.log("frequency:", this.state.frequency);
        // console.log("isInfiniteRecurring:", this.state.isInfiniteRecurring);
        // console.log("recurringOccurrences:", this.state.recurringOccurrences);
        // console.log("Calculated isRecurring:", paymentInfo.isRecurring);
        // console.log("Calculated recurringInterval:", paymentInfo.recurringInterval);
        // console.log("Calculated recurringDay:", paymentInfo.recurringDay);
        // console.log("Calculated recurringEndDate:", paymentInfo.recurringEndDate);
        // console.log("=== END DEBUG ===");

        // If project ID is empty, add projectName based on transaction type
        if (!projectId) {
          const projectName = this.state.transactionType === 'donation' ? 'General Donation' : 'General Payment';
          paymentInfo.projectName = projectName;
        }
        
        // Only include organizationId if it exists
        if (this.state.organization && this.state.organization.organizationId) {
          paymentInfo.organizationId = this.state.organization.organizationId;
        } else {
          // console.warn("organizationId is missing in card payment");
        }

        if (this.state.wallet) {
          /* Stripe specific */
          paymentInfo.wallet = this.state.wallet;
        }
        // console.log(JSON.stringify(paymentInfo) + " -312");
      } else if (this.state.brand === "PayMe" || this.state.brand === "fps" || this.state.brand === "TapAndGo") {
        // console.log(this.state.brand + " -314");
        
        // Note: PayMe and TapAndGo redirect early (above), so this block primarily handles FPS
        // FPS normally routes through ManualPayments, but this handles the edge case
        
        // Use project ID from donations array if available, otherwise use mission ID
        const projectId = this.state.donations && this.state.donations.length > 0 && this.state.donations[0].mission 
          ? this.state.donations[0].mission.id 
          : this.state.mission.id;
        
        
        // Backend calculates fees - just send original amount and coverFee flag
        paymentInfo = {
          projectId: projectId,
          lastName: this.state.donorInfo.lastName,
          firstName: this.state.donorInfo.firstName,
          email: this.state.donorInfo.email,
          phoneNumber: this.state.donorInfo.phoneNumber,
          amount: this.state.amount, // Always send original amount, backend handles fees
          currency: "hkd",
          memo: memo,
          country: this.state.country,
          transactionType: this.state.transactionType || 'donation',
          coverFee: this.state.coverFee || false, // Backend uses this to add fees
          baseAmount: this.state.amount, // Store base amount before fees for editing
          feeBreakdown: this.state.feeBreakdown || null, // Include fee breakdown from state
        };

        // If project ID is empty, add projectName based on transaction type
        if (!projectId) {
          const projectName = this.state.transactionType === 'donation' ? 'General Donation' : 'General Payment';
          paymentInfo.projectName = projectName;
        }
        
        // Only include organizationId if it exists
        if (this.state.organization && this.state.organization.organizationId) {
          paymentInfo.organizationId = this.state.organization.organizationId;
        } else {
          // console.warn("organizationId is missing in PayMe/FPS/TapAndGo payment");
        }
        // console.log("🔥 FRONTEND PAYMENT DATA BEING SENT:");
        // console.log("- Amount:", paymentInfo.amount);
        // console.log("- Original Amount:", paymentInfo.originalAmount);
        // console.log("- Cover Fee:", paymentInfo.coverFee);
        // console.log("- Fee Breakdown:", paymentInfo.feeBreakdown);
        // console.log("- Has Fee Breakdown:", !!paymentInfo.feeBreakdown);
        // console.log("Full payment info:", JSON.stringify(paymentInfo, null, 2));
      }

      // Note: Manual payments do not support recurring transactions
      // Recurring payments are only supported via card payments using the new subscription system
      // (isRecurring, recurringInterval, recurringDay, recurringEndDate)

      paymentInfo.orgName = orgName;
      paymentInfo.payerType = this.state.payerType || 'individual';
      if (this.state.paymentReference) {
        paymentInfo.paymentReference = this.state.paymentReference;
      }

      // Include custom fields
      if (this.state.customFields && Object.keys(this.state.customFields).length > 0) {
        paymentInfo.fields = Object.values(this.state.customFields).map((field) => ({
          id: field.id,
          value: field.value,
        }));
      }

      const fetchPublicPayment = (paymentInfo) => {
        simpleFetch("/user/payments", "POST", paymentInfo).then((res) => {
          this.setState({
            loading: false,
            projectId: paymentInfo.projectId,
          });

          if (res.body.status === "succeeded") {
            this.props.history.push({
              pathname: "/ConfirmPayment",
              state: {
                organization: this.state.organization,
                projectReturnUrl: this.state.mission.projectReturnUrl,
                receiptLink: res.body.receiptLink,
                autoReceipt: res.body.autoReceipt,
                // Lets ConfirmPayment check for a receipt that isn't ready yet
                paymentIntent: res.body.paymentIntent,
                brand: this.state.brand, // Pass the brand information
                isFreePlanFlow: this.getIsFreePlanFlow(),
                isRecurring: paymentInfo.isRecurring,
                scheduledDate: paymentInfo.scheduledDate,
              },
            });
          }

          // Check payment status and handle errors
          if (res.body.status !== "succeeded" && res.body.status !== "requires_action") {
            // Get specific error message from response if available
            const errorMessage = res.body.message || (res.body.error && res.body.error.message) || languageObj.paymentFailErr;
            // console.error("Payment failed:", {
            //   status: res.body.status,
            //   error: errorMessage,
            //   response: res.body
            // });
            this.setState({
              errMsgDetails: errorMessage,
              errMsg: true,
              loading: false
            });
            return; // Exit early on error
          }

          // Handle 3DS authentication if required
          if (res.body.status === "requires_action") {
            this.setState({
              loading: false,
              stripe3DS: res.body.stripe3DS,
              status: res.body.status,
              paymentIntent: res.body.paymentIntent,
              errMsg: false, // Clear any previous errors
              errMsgDetails: "" // Clear error message
            });

            this.pollPaymentIntent("/user/payments", paymentInfo);

          }
        });
        
      };

      const fetchCustomerPayment = (paymentInfo) => {
        simpleFetch("/donors/payments", "POST", paymentInfo).then((res) => {
          this.setState({
            loading: false,
            projectId: paymentInfo.projectId,
          });

          // console.log("Customer payment response:", res.body);

          // Log validation errors if present
          if (res.body.errors) {
            // console.error("Validation errors:", JSON.stringify(res.body.errors, null, 2));
          }

          if (res.body.status === "succeeded") {
            this.props.history.push({
              pathname: "/ConfirmPayment",
              state: {
                organization: this.state.organization,
                projectReturnUrl: this.state.mission.projectReturnUrl,
                receiptLink: res.body.receiptLink,
                autoReceipt: res.body.autoReceipt,
                // Lets ConfirmPayment check for a receipt that isn't ready yet
                paymentIntent: res.body.paymentIntent,
                brand: this.state.brand,
                isFreePlanFlow: this.getIsFreePlanFlow(),
                isRecurring: paymentInfo.isRecurring,
                scheduledDate: paymentInfo.scheduledDate,
              },
            });
          }

          // Check payment status and handle errors
          if (res.body.status !== "succeeded" && res.body.status !== "requires_action") {
            // Get specific error message from response if available
            const errorMessage = res.body.message || (res.body.error && res.body.error.message) || languageObj.paymentFailErr;
            // console.error("Customer payment failed:", {
            //   status: res.body.status,
            //   error: errorMessage,
            //   response: res.body
            // });
            this.setState({
              errMsgDetails: errorMessage,
              errMsg: true,
              loading: false
            });
            return; // Exit early on error
          }

          // Handle 3DS authentication if required
          if (res.body.status === "requires_action") {
            this.setState({
              loading: false,
              stripe3DS: res.body.stripe3DS,
              status: res.body.status,
              paymentIntent: res.body.paymentIntent,
              errMsg: false,
              errMsgDetails: ""
            });

            this.pollPaymentIntent("/donors/payments", paymentInfo);
          }
        })
          .catch(error => {
            // console.error("Customer payment network error:", error);
            this.setState({
              errMsg: true,
              errMsgDetails: "Network error while processing payment. Please try again.",
              loading: false
            });
          });
      };

      // Check if this is a recurring payment (using new subscription system)
      const isRecurringPayment = paymentInfo.isRecurring === true;

      // Recurring payments require authentication - users must register and verify
      // via PersonalDetails before reaching the Review page.
      // One-time payments can be made by guests without authentication.
      if (this.props.isAuth || isRecurringPayment) {
        if (isRecurringPayment && !this.props.isAuth) {
          // This shouldn't happen if PersonalDetails validation works correctly
          // console.error("Recurring payment attempted without authentication");
          this.setState({
            loading: false,
            errMsg: true,
            errMsgDetails: "Authentication required for recurring payments. Please refresh and try again."
          });
          return;
        }
        fetchCustomerPayment(paymentInfo);
      } else {
        // console.log(JSON.stringify(paymentInfo) + " -414");
        paymentInfo.type = "card";
        fetchPublicPayment(paymentInfo);
      }
    }
  }

  // Poll a PaymentIntent through its 3DS challenge, then confirm it and go
  // on to ConfirmPayment. basePath is /user/payments (guest) or
  // /donors/payments (signed in).
  pollPaymentIntent(basePath, paymentInfo) {
    // Set up payment status polling with timeout
    const POLL_INTERVAL = 2000; // 2 seconds
    const MAX_POLL_TIME = 300000; // 5 minutes
    let totalPollTime = 0;
    // Consecutive failed status checks tolerated (~30s) before giving
    // up and handing Confirm back
    const MAX_FAILED_POLLS = 15;
    let failedPolls = 0;
    // One status check at a time and one confirmation per intent. Checks
    // used to overlap, so two could both see "succeeded" and each confirm:
    // the slower confirm's "already recorded" reply (no receipt link) or its
    // 409 could reach the donor first (CP-406)
    let checkInFlight = false;
    let confirmSent = false;

    /* fetch Stripe payment status every two seconds */
    this.retrievePaymentStatus = setInterval(() => {
      if (totalPollTime >= MAX_POLL_TIME) {
        clearInterval(this.retrievePaymentStatus);
        if (this.isComponentMounted) {
          this.setState({
            errMsg: true,
            errMsgDetails: "Payment authentication timed out. Please try again.",
            loading: false,
            // Drop the 3DS overlay, or it keeps covering this error
            status: "",
            staleIntent: this.getStaleIntent(basePath, paymentInfo)
          });
        }
        return;
      }

      totalPollTime += POLL_INTERVAL;

      if (checkInFlight || confirmSent) return;
      checkInFlight = true;

      simpleFetch(`${basePath}/retrievePaymentStatus`, "POST", {
        paymentIntent: this.state.paymentIntent,
        projectId: this.state.projectId,
        // A donation to the organization has no project: without this the
        // check was refused and the charge never confirmed (CP-407)
        organizationId: paymentInfo.organizationId,
      })
        .catch((error) => {
          // A network failure of the status check itself, as opposed to
          // a failed authentication or confirmation further down
          error.isPollFailure = true;
          throw error;
        })
        .then((res) => {
          checkInFlight = false;
          // Check if component is still mounted before updating state
          if (!this.isComponentMounted) {
            clearInterval(this.retrievePaymentStatus);
            return;
          }


          if (res.status !== 200) {
            // A 5xx/412 has no status; without this it cleared the
            // overlay while polling carried on
            const error = new Error("Payment status check failed");
            error.isPollFailure = true;
            throw error;
          }
          failedPolls = 0;

          this.setState({
            status: res.body.status,
            // Past the challenge but not settled (e.g. "processing"): the
            // overlay is gone, so cover the page, or Confirm comes back and
            // a click starts a second payment (CP-408)
            loading: isSettling(res.body.status),
          });

          if (res.body.status === "succeeded") {
            clearInterval(this.retrievePaymentStatus);
            confirmSent = true;
            this.setState({
              loading: true,
              errMsg: false,
              errMsgDetails: ""
            });

            // Confirm 3DS payment success
            return simpleFetch(`${basePath}/confirm3DSPayment`, "POST", {
              ...paymentInfo,
              paymentIntent: this.state.paymentIntent,
              projectId: this.state.projectId,
            });
          } else if (res.body.status === "requires_payment_method") {
            const error = new Error("Payment authentication failed");
            error.isAuthFailed = true;
            throw error;
          }
        })
        .then((res) => {
          // Check if component is still mounted before navigation
          if (!this.isComponentMounted) {
            return;
          }

          if (res && res.body) { // Only proceed if we got a response
            // Only a 200 means the payment was recorded: a 409 (held or
            // needing checking), 412 or 500 isn't a success (CP-387)
            if (res.status !== 200) {
              throw new Error(
                (typeof res.body === "object" && res.body.message) ||
                  (this.props.languageObj || {}).paymentFailErr
              );
            }
            this.props.history.push({
              pathname: "/ConfirmPayment",
              state: {
                organization: this.state.organization,
                projectReturnUrl: this.state.mission && this.state.mission.projectReturnUrl,
                receiptLink: res.body.receiptLink,
                autoReceipt: res.body.autoReceipt,
                // Lets ConfirmPayment check for a receipt that isn't ready yet
                paymentIntent: res.body.paymentIntent || this.state.paymentIntent,
                brand: this.state.brand,
                isFreePlanFlow: this.getIsFreePlanFlow(),
                isRecurring: paymentInfo.isRecurring,
                scheduledDate: paymentInfo.scheduledDate,
              },
            });
          }
        })
        .catch((error) => {
          checkInFlight = false;
          if (!this.isComponentMounted) {
            return;
          }

          // Ride out a blip: the payment may still complete, and
          // re-enabling Confirm now would invite a second charge
          if (error.isPollFailure && ++failedPolls < MAX_FAILED_POLLS) {
            return;
          }

          clearInterval(this.retrievePaymentStatus);
          // console.error("Payment status error:", error);
          this.setState({
            errMsg: true,
            errMsgDetails: error.message || "Payment authentication failed. Please try again.",
            loading: false,
            // Giving up: drop the overlay so the error shows and the
            // donor is not stuck behind it
            status: "",
            // Unless the card was declined, the intent may still succeed
            // (or already has, if only the confirmation failed)
            staleIntent: error.isAuthFailed
              ? null
              : this.getStaleIntent(basePath, paymentInfo)
          });
        });
    }, POLL_INTERVAL);
  }

  // What a retry needs to pick an abandoned intent back up: the details it
  // was created with, so a fee toggle since can't confirm it at a new amount
  getStaleIntent(basePath, paymentInfo) {
    return {
      basePath,
      paymentInfo,
      paymentIntent: this.state.paymentIntent,
      stripe3DS: this.state.stripe3DS,
    };
  }

  // Before a retry creates a new PaymentIntent, check the one Review gave up
  // on. Declined or cancelled: start afresh. Anything else may still charge,
  // so resume it; neither status endpoint returns the challenge URL, hence
  // the stored stripe3DS.
  resumeStaleIntent() {
    const { basePath, paymentInfo, paymentIntent, stripe3DS } = this.state.staleIntent;
    this.setState({ loading: true, errMsg: false, errMsgDetails: "" });

    simpleFetch(`${basePath}/retrievePaymentStatus`, "POST", {
      paymentIntent,
      projectId: this.state.projectId,
      organizationId: paymentInfo.organizationId,
    })
      .then((res) => {
        if (!this.isComponentMounted) return;
        if (res.status !== 200) throw new Error("Payment status check failed");

        const { status } = res.body;
        if (status === "requires_payment_method" || status === "canceled") {
          this.setState({ staleIntent: null, loading: false }, () =>
            this.onNextBtnClicked({ preventDefault() {} })
          );
          return;
        }

        this.setState({
          staleIntent: null,
          paymentIntent,
          stripe3DS,
          status,
          // Only a live challenge needs the overlay; otherwise wait it out
          // behind the loader until it settles
          loading: status !== "requires_action",
        });
        this.pollPaymentIntent(basePath, paymentInfo);
      })
      .catch(() => {
        if (!this.isComponentMounted) return;
        // Keep the stale intent: creating a new one blind is the risk
        this.setState({
          loading: false,
          errMsg: true,
          errMsgDetails: this.props.languageObj.previousPaymentCheckErr,
        });
      });
  }

  errRender(bool, errStr, testid) {
    if (!errStr || !bool) return null;
    return (
      <div className="Review-errorContainer" data-testid={testid}>{renderErrorMsg(errStr)}</div>
    );
  }

  render3DS() {
    if (this.state.status === "requires_action") {
      return (
        <div className="Review-iframeContainer">
          <iframe
            className="Review-iframe"
            src={this.state.stripe3DS}
            title="3D Secure"
            width="100%"
            height="100%"
          />
        </div>
      );
    } else return <div></div>;
  }

  // Helper function to get payment method type for fee calculation
  getPaymentMethodTypeForFee() {
    const { brand } = this.state;

    // Map payment methods to fee calculation types
    const paymentMethodMap = {
      // Credit card brands
      'Visa': 'creditCard',
      'Mastercard': 'creditCard',
      'American Express': 'creditCard',
      'JCB': 'creditCard',
      'UnionPay': 'creditCard',
      // Default for any card payment
      'card': 'creditCard',

      // Stripe digital wallets
      'Apple Pay': 'creditCard',
      'Google Pay': 'creditCard',
      'Link': 'creditCard',

      // PayMe has its own rate (organization-configurable, default 1.5%)
      'PayMe': 'PayMe',

      // Manual payment methods (zero transaction fees)
      'alipay': 'manual',
      'alipay_hk': 'manual',
      'alipay_ch': 'manual',
      'wechatpay': 'manual',
      'wechatpay_hk': 'manual',
      'wechatpay_ch': 'manual',
      'bocpay': 'manual',
      'FPS': 'manual',
      'fps': 'manual',
      'TapAndGo': 'manual',
      'octopus': 'manual',
      'bank_transfer': 'manual',
      'cheque': 'manual',
      'check_writing': 'manual',
      'cash': 'manual',
      'paypal': 'manual',
    };

    return paymentMethodMap[brand] || 'creditCard'; // Default to credit card fees
  }

  // Helper function to get payment processor label for fee display
  getProcessingFeeLabel() {
    const { brand } = this.state;
    const languageObj = this.props.languageObj || {};

    // Get payment method type (already categorizes manual vs creditCard vs PayMe)
    const paymentMethodType = this.getPaymentMethodTypeForFee();

    // Manual payment methods use generic label
    if (paymentMethodType === 'manual') {
      return languageObj.processingFee || 'Processing Fee';
    }

    // PayMe uses HSBC as payment processor
    if (brand === 'PayMe' || paymentMethodType === 'PayMe') {
      return languageObj.hsbcProcessingFee || 'HSBC Processing Fee';
    }

    // Credit cards use Stripe
    return languageObj.stripeProcessingFee || 'Stripe Processing Fee';
  }

  // TODO: Future enhancement - retrieve card issuer country from Stripe
  // This would require backend integration to fetch payment method details
  // and determine if the card was issued in Hong Kong or elsewhere
  /*
  async getCardIssuerCountry(paymentMethodId) {
    try {
      const response = await simpleFetch(`/payments/card-country/${paymentMethodId}`, 'GET');
      return response.body.country; // 'HK' or other country code
    } catch (error) {
      // console.warn('Could not retrieve card issuer country:', error);
      return null;
    }
  }
  */

  // Get fee breakdown from state (populated by fetchFeeBreakdown)
  getFeeBreakdown() {
    if (!this.state.amount) return null;
    return this.state.feeBreakdown || null;
  }

  // Fetch fee breakdown from backend API
  async fetchFeeBreakdown() {

    const amountValue = Number(this.state.amount);

    if (!amountValue || Number.isNaN(amountValue) || amountValue <= 0) {
      if (this.isComponentMounted) {
        this.setState({
          feeBreakdown: null,
          feeCalculationError: null,
          isFeeLoading: false,
          hasFetchedFeeBreakdown: true,
        });
      }
      return;
    }

    const paymentMethodType = this.getPaymentMethodTypeForFee();
    const cardBrand = this.state.brand;
    const cardCountry = this.state.cardCountry;
    const transactionCountry = this.state.country || 'HK';

    // Get organizationId from various possible sources
    const organizationId = (this.state.organization && this.state.organization.organizationId)
      || (this.state.organization && this.state.organization.id)
      || this.state.projectOrgId
      || this.state.organizationId
      || (this.state.mission && this.state.mission.organizationId)
      || (this.state.donations && this.state.donations[0] && this.state.donations[0].mission && this.state.donations[0].mission.organizationId);

    if (!organizationId) {
      // console.error('Organization ID not found in state:', {
      //  organization: this.state.organization,
      //  projectOrgId: this.state.projectOrgId,
      //  organizationId: this.state.organizationId,
      //  mission: this.state.mission,
      //  donations: this.state.donations
      // });
      this.setState({
        feeBreakdown: null,
        feeCalculationError: 'Unable to calculate fees - organization not found',
        isFeeLoading: false,
        hasFetchedFeeBreakdown: true,
      });
      return;
    }

    // The project is needed for the fee-coverage policy, not just the fee: the
    // backend only consults the project's feeCoverageMode when a projectId is
    // supplied, and falls back to the org mode alone otherwise. Omitting it
    // made a project-level override invisible here, so a project that enables
    // coverage under a 'disabled' org quoted the donor an un-covered total
    // while the charge itself honoured the override (CP-281).
    const feeProjectId =
      this.state.donations && this.state.donations.length > 0 && this.state.donations[0].mission
        ? this.state.donations[0].mission.id
        : (this.state.mission && this.state.mission.id) || this.state.projectId;

    const feeData = {
      amount: amountValue,
      organizationId, // Backend fetches plan and volume from database
      ...(feeProjectId && { projectId: feeProjectId }),
      transactionCountry,
      paymentMethod: paymentMethodType,
      coverFee: this.state.coverFee,
      cardBrand,
      cardCountry,
    };


    const requestId = ++this.latestFeeRequestId;

    if (this.isComponentMounted) {
      this.setState({
        isFeeLoading: true,
        feeCalculationError: null,
      });
    }

    try {
      const res = await simpleFetch("/user/payments/calculate-fees", "POST", feeData);

      if (!this.isComponentMounted || requestId !== this.latestFeeRequestId) {
        return;
      }

      if (!res) {
        // console.error('Fee breakdown response missing');
        this.setState({
          feeBreakdown: null,
          feeCalculationError: 'Failed to calculate transaction fees',
          isFeeLoading: false,
          hasFetchedFeeBreakdown: true,
        });
        return;
      }


      const isValidPayload =
        res.status === 200 &&
        res.body &&
        !res.body.error &&
        typeof res.body.totalAmount === 'number';

      if (isValidPayload) {
        this.setState({
          feeBreakdown: res.body,
          feeCalculationError: null,
          isFeeLoading: false,
          hasFetchedFeeBreakdown: true,
        });
      } else {
        const responseBody = res ? res.body : null;
        const errorMessage =
          (responseBody && responseBody.message) ||
          (responseBody && responseBody.error) ||
          'Failed to calculate transaction fees';
        // console.error('Fee breakdown unavailable:', errorMessage);
        this.setState({
          feeBreakdown: null,
          feeCalculationError: errorMessage,
          isFeeLoading: false,
          hasFetchedFeeBreakdown: true,
        });
      }
    } catch (error) {
      if (!this.isComponentMounted || requestId !== this.latestFeeRequestId) {
        return;
      }

      // console.error('Error fetching fee breakdown:', error);
      this.setState({
        feeBreakdown: null,
        feeCalculationError: error && error.message ? error.message : 'Failed to calculate transaction fees',
        isFeeLoading: false,
        hasFetchedFeeBreakdown: true,
      });
    }
  }

  render() {
    const isInitialFeeLoading = this.state.isFeeLoading && !this.state.hasFetchedFeeBreakdown;
    if (
      this.state.loading ||
      this.props.location.state === null ||
      this.props.location.state === undefined ||
      isInitialFeeLoading
    ) {
      return <this.props.RenderFullPageLoading />;
    }

    // Removed duplicate onScroll definition - already defined and bound in constructor
    // this.onScroll is already bound and added to window scroll listener in componentDidMount

    const languageObj = this.props.languageObj;

    // Check if this is a free plan flow - use the getIsFreePlanFlow method for consistency
    const isFreePlanFlow = this.getIsFreePlanFlow();

    // Set class names based on flow type
    const viewClassName = isFreePlanFlow ? "Review-view Review-view--free" : "Review-view";
    const headerClassName = isFreePlanFlow ? "Review-header Review-header--free" : "Review-header";
    const mainClassName = isFreePlanFlow ? "Review-main Review-main--free" : "Review-main";
    const stepsContainerClass = isFreePlanFlow ? "Review-stepsContainer Review-stepsContainer--free" : "Review-stepsContainer";
    const backContainerClass = isFreePlanFlow ? "Review-backContainer Review-backContainer--free" : "Review-backContainer";
    const stepTextClass = isFreePlanFlow ? "Review-stepTxt Review-stepTxt--free" : "Review-stepTxt";
    const indicatorClass = isFreePlanFlow ? "Review-indicator Review-indicator--free" : "Review-indicator";
    const inputContainerClass = isFreePlanFlow ? "Review-inputContainer Review-inputContainer--free" : "Review-inputContainer";
    const nextBtnClass = isFreePlanFlow ? "Review-nextBtn Review-nextBtn--free" : "Review-nextBtn";

    const headerOverrideStyle = {
      background: "#" + this.state.organization.headerColor,
    };

    const resolved = resolveThemeColors(this.state.organization.headerColor, this.state.organization.buttonColor);

    const nextBtnStyle = {
      background: "#" + resolved.headerColor,
      color: "#" + resolved.buttonColor,
    };

    // Check if scheduledDate exists and is valid
    const isValidDate = (date) => {
      return date && !isNaN(new Date(date).getTime());
    };

    // Determine which date to use for display
    // For recurring payments, prefer startDate if available
    // For one-time payments, use scheduledDate
    let scheduledDate;

    if (isValidDate(this.state.startDate)) {
      // Use startDate if available (set by DatePicker for recurring payments)
      scheduledDate = new Date(this.state.startDate);
      //console.log("Review: Using startDate for display:", scheduledDate);
    } else if (isValidDate(this.state.scheduledDate)) {
      // Fall back to scheduledDate (for one-time or legacy flows)
      scheduledDate = new Date(this.state.scheduledDate);
      // console.log("Review: Using scheduledDate for display:", scheduledDate);
    } else {
      // This should not happen - log error and use current date as last resort
      // console.error("ERROR: No valid date found in Review! startDate:", this.state.startDate, "scheduledDate:", this.state.scheduledDate);
      scheduledDate = new Date();
      //console.warn("Review: Falling back to current date (this should not happen)");
    }

    let year = scheduledDate.getFullYear();
    let month = scheduledDate.getMonth() + 1;
    let day = scheduledDate.getDate();

    let recurringOccurrences = this.state.recurringOccurrences || 0;
    let newMonth = Number(month) + Number(recurringOccurrences);
    let newYear = year;

    if (newMonth > 12 && newMonth <= 24) {
      newMonth = newMonth - 12;
      newYear = newYear + 1;
    }

    if (newMonth > 24 && newMonth <= 36) {
      newMonth = newMonth - 24;
      newYear = newYear + 2;
    }

    const startDate = year + "/" + month + "/" + day;
    const endDate = newYear + "/" + newMonth + "/" + day;

    // Removed console.log from render method to prevent spam on every re-render
    // console.log("Date debugging:", {
    //   originalScheduledDate: this.state.scheduledDate,
    //   isValid: isValidDate(this.state.scheduledDate),
    //   usedDate: scheduledDate,
    //   formattedStartDate: startDate,
    //   formattedEndDate: endDate
    // });

    const RenderQrLink = () => {
      const qrLink = this.state.qrLink;
      if (!qrLink) return null;

      return (
        <div>
          <div style={{ textAlign: "center" }}>
            <QRCodeCanvas value={qrLink} />
          </div>
        </div>
      );
    };

    const { donorInfo } = this.state;


    let lastName;

    if (this.state.wallet) {
      if (!this.state.donorInfo) {
        lastName = this.state.lastName;
      } else {
        lastName = this.state.donorInfo.lastName;
      }
    } else {
      lastName = this.state.donorInfo ? this.state.donorInfo.lastName : this.state.lastName;
    }

    let firstName;

    if (this.state.wallet) {
      if (!this.state.donorInfo) {
        firstName = this.state.firstName;
      } else {
        firstName = this.state.donorInfo.firstName;
      }
    } else {
      firstName = this.state.donorInfo ? this.state.donorInfo.firstName : this.state.firstName;
    }

    let email;

    if (this.state.wallet) {
      if (!this.state.donorInfo) {
        email = this.state.email;
      } else {
        email = this.state.donorInfo.email;
      }
    } else {
      email = this.state.donorInfo ? this.state.donorInfo.email : this.state.email;
    }

    let phoneNumber;

    if (this.state.wallet) {
      if (!this.state.donorInfo) {
        phoneNumber = this.state.phoneNumber;
      } else {
        phoneNumber = this.state.donorInfo.phoneNumber;
      }
    } else {
      phoneNumber = this.state.donorInfo ? this.state.donorInfo.phoneNumber : this.state.phoneNumber;
    }

    const { orgName } = donorInfo || { orgName: "" };
    const { memo } = donorInfo || { memo: "" };
    // console.log(JSON.stringify(this.state) + " -624");

    const paymentMethodNames = {
      fps: "FPS",
      FPS: "FPS",
      PayMe: "PayMe",
      paypal: "PayPal",
      alipay: "Alipay",
      alipay_hk: "AlipayHK (Hong Kong)",
      alipay_ch: "Alipay (China)",
      wechatpay: "WeChat Pay",
      wechatpay_hk: "WeChat Pay HK (Hong Kong)",
      wechatpay_ch: "WeChat Pay (China)", 
      bocpay: "BoC Pay",
      octopus: "Octopus",
      bank_transfer: "Bank Transfer",
      check_writing: "Cheque",
      cash: "Cash",
      paypal_manual: "PayPal",
      TapAndGo: "Tap & Go",
    };
    

    const { brand, fileUploaded } = this.state;
    // const isDisabled = this.state.buttonClick;

    // eslint-disable-next-line no-undef
    const manualPaymentMethods = new Set([
      "alipay",
      "alipay_hk",
      "alipay_ch",
      "wechatpay",
      "wechatpay_hk",
      "wechatpay_ch",
      "bocpay",
      "octopus",
      "fps",
      "bank_transfer",
      "check_writing",
      "cash",
      "paypal_manual",
    ]);

    const isFeeLoading = this.state.isFeeLoading;
    // A 3DS challenge is already in flight; a second click would open a
    // second PaymentIntent and a second polling loop behind the overlay.
    const is3DSPending = this.state.status === "requires_action";
    const isConfirmDisabled =
      isFeeLoading ||
      is3DSPending ||
      (manualPaymentMethods.has(brand) && !fileUploaded);

    const confirmButtonLabel = isFeeLoading
      ? languageObj.loadBtn
      : this.state.buttonClick
        ? languageObj.nextBtn
        : languageObj.loadBtn;

    const getPaymentMethodDisplayName = () => {
      const { brand, wallet, last4, methodKey } = this.state;

      // console.log(methodKey + " -598");

      // First check if we have a specific methodKey that should be displayed (like alipay_hk)
      if (methodKey && paymentMethodNames[methodKey]) {
        return paymentMethodNames[methodKey];
      }

      // Then check for wallet
      const walletDisplayName = formatWalletPaymentMethod(wallet, brand, last4);
      if (walletDisplayName) {
        return walletDisplayName;
      }

      // Otherwise use the brand
      if (brand && paymentMethodNames[brand]) {
        return paymentMethodNames[brand];
      }

      // Fallback for direct card payments
      if (brand) {
        return `${brand.charAt(0).toUpperCase() + brand.slice(1)} ••••${last4 || '****'}`;
      }

      // Ultimate fallback if no brand information
      return last4 ? `Card ••••${last4}` : 'Card Payment';
    };

    

    // Create proper logo style based on the type of logo property
    const createLogoStyle = (logo) => {
      if (typeof logo === 'string') {
        // If logo is a URL string, create a backgroundImage style
        return { 
          backgroundImage: `url('${logo}')`, 
          backgroundSize: 'contain', 
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center'
        };
      } else if (typeof logo === 'object') {
        // If logo is already a style object, use it directly
        return logo;
      }
      // Default empty object if logo is undefined or another type
      return {};
    };
    
    const logoStyle = createLogoStyle(this.state.organization.logo);

    const feeBreakdown = this.getFeeBreakdown();
    const hasFeeBreakdown = !!(feeBreakdown && typeof feeBreakdown === 'object');
    const showFeeDetails = hasFeeBreakdown;
    const platformFeeValue = showFeeDetails && feeBreakdown.serviceFee != null
      ? feeBreakdown.serviceFee
      : null;
    const processingFeeValue = showFeeDetails
      ? (feeBreakdown.processingFee != null
        ? feeBreakdown.processingFee
        : (feeBreakdown.stripeFee != null ? feeBreakdown.stripeFee : null))
      : null;

    const formatHKD = (value) => (
      <span className="Review-amount">
        <span className="Review-amountCurrency">HK$</span>
        <NumericFormat
          decimalSeparator="."
          displayType="text"
          valueIsNumericString={true}
          value={value}
          allowNegative={false}
          thousandSeparator={true}
          prefix={""}
          fixedDecimalScale={true}
          decimalScale={2}
          renderText={(formattedValue) => (
            <span className="Review-amountValue">{formattedValue}</span>
          )}
        />
      </span>
    );

    const totalPaymentAmount = showFeeDetails && feeBreakdown.totalAmount != null
      ? feeBreakdown.totalAmount
      : this.state.amount;
    const includedFeesAmount = showFeeDetails && feeBreakdown.totalFee != null
      ? feeBreakdown.totalFee
      : 0;
    const nonprofitReceivesAmount = showFeeDetails && feeBreakdown.nonprofitReceives != null
      ? feeBreakdown.nonprofitReceives
      : null;
    const showIncludedFees = showFeeDetails && includedFeesAmount !== null && includedFeesAmount !== undefined;
    const showFeeBreakdownDetails = showFeeDetails && this.state.coverFee === true; // Show fee breakdown tooltip only when user opts to cover fees
    const feesTooltipItems = [];

    if (platformFeeValue !== null) {
      feesTooltipItems.push({
        label: languageObj.checkPlusPlatformFee || "CheckPlus Platform Fee",
        value: platformFeeValue
      });
    }
    if (processingFeeValue !== null) {
      feesTooltipItems.push({
        label: this.getProcessingFeeLabel(),
        value: processingFeeValue
      });
    }
    if (
      hasFeeBreakdown &&
      feesTooltipItems.length === 0 &&
      feeBreakdown &&
      feeBreakdown.totalFee !== undefined &&
      feeBreakdown.totalFee !== null
    ) {
      feesTooltipItems.push({
        label: (languageObj.fees && languageObj.fees.totalFees) || "Total fees",
        value: feeBreakdown.totalFee
      });
    }

    // Function to render the header for free plan users
    const renderFreeHeader = () => {
      if (!isFreePlanFlow) return null;
      return (
        <div className={headerClassName}>
          <div className="Review-freeBrand">
            <span className="Review-freeBrandPowered">Powered by</span>
            <img
              src={PLATFORM === "plug" ? plugLogo : checkplusLogo}
              alt={PLATFORM === "plug" ? "Plug" : "CheckPlus for Nonprofits"}
              className="Review-freeBrandLogoImage"
            />
          </div>
          <div className="Review-stepsContainer Review-stepsContainer--free">
            <div className="Review-stepsContent">
              <div className={stepTextClass}>
                {languageObj.step4of4 || "Step 4 of 4"}
              </div>
              <div className={indicatorClass}>
                <div className="Review-circle Review-circleSelected" />
                <div className="Review-circle Review-circleSelected" />
                <div className="Review-circle Review-circleSelected" />
                <div className="Review-circle Review-circleSelected" />
              </div>
            </div>
          </div>
          <div className="Review-freeHeaderNote">
            {languageObj.secureCheckout || "Secure Checkout"}
          </div>
        </div>
      );
    };

    return (
      <div className={viewClassName} style={isFreePlanFlow ? {} : headerOverrideStyle}>
        {this.render3DS()}
        <Helmet>
          <title>{this.state.organization.organizationName}</title>
        </Helmet>

        {isFreePlanFlow ? renderFreeHeader() : (
          <div className={headerClassName} style={headerOverrideStyle}>
            <div className="Review-logo" style={logoStyle}>
              {!this.state.organization.logo ? "L O G O" : ""}
            </div>
          </div>
        )}

        <div className={mainClassName}>
          {!isFreePlanFlow && (
            <div className={stepsContainerClass}>
              <div
                className={backContainerClass}
                data-testid="review-back-button"
                onClick={this.onBackBtnClicked}
              >
                <div className="Review-back">
                  <div className="Review-backBtn" />
                  <div className="Review-backText">{languageObj.backText}</div>
                </div>
              </div>
              <div className={stepTextClass} data-testid="review-step-text">
                {this.state.mission.status === "unlisted"
                  ? (languageObj.step4of4 || "Step 4 of 4")
                  : languageObj.stepTxt}
              </div>
              <div className="Review-indicator">
                {this.state.mission.status === "unlisted" ? (
                  // 4 steps for unlisted projects, with all four highlighted
                  <>
                    <div className="Review-circle Review-circleSelected Review-marginRight" />
                    <div className="Review-circle Review-circleSelected Review-marginRight" />
                    <div className="Review-circle Review-circleSelected Review-marginRight" />
                    <div className="Review-circle Review-circleSelected" />
                  </>
                ) : (
                  // 5 steps for open projects, with all five highlighted (original behavior)
                  <>
                    <div className="Review-circle Review-circleSelected Review-marginRight" />
                    <div className="Review-circle Review-circleSelected Review-marginRight" />
                    <div className="Review-circle Review-circleSelected Review-marginRight" />
                    <div className="Review-circle Review-circleSelected Review-marginRight" />
                    <div className="Review-circle Review-circleSelected" />
                  </>
                )}
              </div>

              {this.state.isScrolling ? (
                <div
                  className="Review-smallLogoContainer Review-fadeIn"
                  style={headerOverrideStyle}
                >
                  <div
                    className="Review-logoS"
                    style={logoStyle}
                  ></div>
                </div>
              ) : (
                <div></div>
              )}
            </div>
          )}

          <div className={inputContainerClass}>
            {isFreePlanFlow && (
              <div className={backContainerClass} onClick={this.onBackBtnClicked}>
                <div className="Review-back">
                  <div className="Review-backBtn" />
                  <div className="Review-backText">{languageObj.backText}</div>
                </div>
              </div>
            )}

            <div
              className="Review-bubble"
              style={!isFreePlanFlow ? { marginTop: '50px' } : undefined}
            >
              <div className="Review-bubbleMessage">
                <div className="Review-redDot" />
                <div className="Review-bubbleText">
                  {languageObj.warning}
                </div>
              </div>
            </div>

            {this.errRender(
              this.state.errMsg,
              this.state.errMsgDetails,
              "review-submit-error"
            )}
            {this.state.feeCalculationError && (
              <div className="Review-errorContainer">
                {renderErrorMsg(this.state.feeCalculationError)}
              </div>
            )}
            <div className="Review-headingText">{languageObj.headingText}</div>
            <div className="Review-subText">{languageObj.subText}</div>
            <div className="Review-summaryGrid">
              {this.state.frequency === "MONTHLY" ? (
                <div className="Review-item">
                  {languageObj.item[1]}
                  <div className="Review-answer">
                    {this.state.isInfiniteRecurring ? (
                      <span>{startDate} {languageObj.item[2]} {languageObj.infiniteRecurring || "Continue until I cancel"}</span>
                    ) : (
                      <span>{startDate} {languageObj.item[2]} {endDate}</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="Review-item">
                  {languageObj.item[0]}
                  <div className="Review-answer">{startDate}</div>
                </div>
              )}
              {this.state.frequency === "MONTHLY" && !this.state.isInfiniteRecurring ? (
                <div className="Review-item">
                  {languageObj.item[3]}
                  <div className="Review-answer">
                    {this.state.recurringOccurrences} {""}
                    {this.state.recurringOccurrences > 1 ? (
                      <span>{languageObj.item[4]}</span>
                    ) : (
                      <span>{languageObj.item[5]}</span>
                    )}
                  </div>
                </div>
              ) : null}

              <div className="Review-item">
                {languageObj.item[6]}
                <div className="Review-answer">
                  {/* If donations exist and there's at least one donation with a mission, use that mission's title */}
                  {this.state.donations && this.state.donations.length > 0 && this.state.donations[0].mission
                    ? getProjectTitle(this.props.language, this.state.donations[0].mission)
                    : getProjectTitle(this.props.language, this.state.mission)}
                </div>
              </div>

              {/* Transaction Type */}
              {this.state.transactionType && (
                <div className="Review-item">
                  {languageObj.item[14] || "Transaction Type"}
                  <div className="Review-answer">
                    {this.state.transactionType === 'donation'
                      ? ((languageObj.transactionTypes && languageObj.transactionTypes.donation) || "Donation")
                      : ((languageObj.transactionTypes && languageObj.transactionTypes.payment) || "Payment")}
                  </div>
                </div>
              )}

              <div className="Review-item">
                {languageObj.item[7]}
                <div className="Review-answer">
              HK
                  <NumericFormat
                    decimalSeparator="."
                    displayType="text"
                    valueIsNumericString={true}
                    value={this.state.coverFee && feeBreakdown ? feeBreakdown.totalAmount : this.state.amount}
                    allowNegative={false}
                    thousandSeparator={true}
                    prefix={"$"}
                    fixedDecimalScale={true}
                    decimalScale={2}
                  />
                </div>
              </div>

              <div className="Review-item">
                {languageObj.item[8]}
                <div className="Review-answer">
                  {getPaymentMethodDisplayName()}
                  {/* Wallet donors skip /SelectPayment entirely, so this is the
                      first and only place they can see - or change - the method
                      that is about to be charged. */}
                  <span
                    className="Review-changePaymentMethod"
                    onClick={this.onChangePaymentMethodClicked}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") this.onChangePaymentMethodClicked();
                    }}
                  >
                    {languageObj.changePaymentMethod || "Change"}
                  </span>
                </div>
              </div>

              <div className="Review-item">
                {languageObj.item[9]}
                <div className="Review-answer">
                  {firstName} {lastName ? " " + lastName : null}
                </div>
              </div>

              <div className="Review-item">
                {languageObj.item[10]}
                <div className="Review-answer">{email}</div>
              </div>

              {orgName && (
                <div className="Review-item">
                  {languageObj.item[13]}
                  <div className="Review-answer">{orgName}</div>
                </div>
              )}

              <div className="Review-item">
                {languageObj.item[11]}
                <div className="Review-answer">{phoneNumber}</div>
              </div>

              {memo && (
                <div className="Review-item">
                  {languageObj.item[12]}
                  <div className="Review-answer">{memo}</div>
                </div>
              )}

              {this.state.paymentReference && (
                <div className="Review-item">
                  {languageObj.paymentReferenceLabel || "Payment Reference"}
                  <div className="Review-answer">{this.state.paymentReference}</div>
                </div>
              )}

              {this.state.customFields && Object.values(this.state.customFields).map((field) => {
                if (!field.value) return null;
                const fieldLabel = field.i18n && field.i18n.label
                  ? (field.i18n.label[this.state.language || 'en'] || field.i18n.label.en || field.id)
                  : field.id;

                let displayValue = field.value;
                if (field.type === 'yesno') {
                  const lang = this.state.language || 'en';
                  if (lang === 'zh' || lang === 'cn') {
                    displayValue = field.value === 'yes' ? '是' : '否';
                  } else {
                    displayValue = field.value === 'yes' ? 'Yes' : 'No';
                  }
                } else if (field.type === 'address') {
                  try {
                    const parsed = JSON.parse(field.value);
                    const isHK = (parsed.country || 'Hong Kong') === 'Hong Kong';
                    if (isHK) {
                      const regionObj = hkRegions.find(r => r.id === parsed.region);
                      const districts = parsed.region ? getDistrictsByRegion(parsed.region) : [];
                      const districtObj = districts.find(d => d.id === parsed.district);
                      const parts = [
                        parsed.flat && `Flat ${parsed.flat}`,
                        parsed.floor && `${parsed.floor}/F`,
                        parsed.block,
                        parsed.building,
                        parsed.street,
                        districtObj && districtObj.name_en,
                        regionObj && regionObj.name_en,
                        regionObj && regionObj.id !== 'hk' && 'HONG KONG',
                      ].filter(Boolean);
                      displayValue = parts.join(', ');
                    } else {
                      displayValue = [
                        parsed.address1, parsed.address2,
                        parsed.city, parsed.state, parsed.postalCode,
                        parsed.country,
                      ].filter(Boolean).join(', ');
                    }
                  } catch (e) {
                    displayValue = field.value;
                  }
                }

                return (
                  <div key={field.id} className="Review-item">
                    {fieldLabel}
                    <div className="Review-answer">{displayValue}</div>
                  </div>
                );
              })}
            </div>

            <div className="Review-item">
              <RenderQrLink />
            </div>

            <div className="Review-bottomSpacer" />
          </div>
          <div className="Review-bottomBar">
            <div className="Review-bottomBarInner">
              <div className="Review-bottomInfo">
                <div className="Review-bottomLabel">
                  {languageObj.totalPayment || "Total payment"}
                </div>
                <div className="Review-bottomAmount" data-testid="review-total-payment">{formatHKD(totalPaymentAmount)}</div>
                {showFeeBreakdownDetails && (
                  <div className="Review-bottomSecondary">
                    {showIncludedFees && feesTooltipItems.length > 0 && (
                      <div
                        className="Review-bottomSubtext Review-bottomSubtext--interactive"
                        data-testid="review-included-fees"
                      >
                        <span>
                          {(languageObj.fees && languageObj.fees.includes) || "Includes"} <span className="Review-bottomAmountInline">{formatHKD(includedFeesAmount)}</span> {(languageObj.fees && languageObj.fees.feesLabel) || "fees"}
                        </span>
                        <span className="Review-bottomInfoIcon" aria-hidden="true">ⓘ</span>
                        <div className="Review-feeTooltip" role="tooltip">
                          {feesTooltipItems.map((item, index) => (
                            <div className="Review-feeTooltipRow" key={`${item.label}-${index}`}>
                              <span className="Review-feeTooltipLabel">{item.label}</span>
                              <span className="Review-feeTooltipValue">{formatHKD(item.value)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {nonprofitReceivesAmount !== null && nonprofitReceivesAmount !== undefined && (
                      <div className="Review-bottomSubtext Review-bottomSubtext--nonprofit">
                        {(languageObj.fees && languageObj.fees.nonprofitReceives) || "Nonprofit receives"} {formatHKD(nonprofitReceivesAmount)}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="Review-bottomActions">
                <div className="Review-bottomNotice">
                  {languageObj.agreement[0]}
                  <a
                    href="https://check.plus/EnPrivacyPolicy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {languageObj.agreement[1]}
                  </a>
                  {languageObj.agreement[2]}
                </div>
                <button
                  type="button"
                  className={`${nextBtnClass} ${isConfirmDisabled ? 'Review-nextBtn-disabled' : ''}`}
                  data-testid="review-next-button"
                  onClick={isConfirmDisabled ? null : this.onNextBtnClicked}
                  style={isConfirmDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : (isFreePlanFlow ? {} : nextBtnStyle)}
                  disabled={isConfirmDisabled}
                >
                  {confirmButtonLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
Review.propTypes = {
  stripe: PropTypes.object.isRequired,
  language: PropTypes.string,
  languageObj: PropTypes.object,
  isAuth: PropTypes.bool,
};

// Wrapper component to use Stripe hooks with class component
function ReviewWithStripe(props) {
  const stripe = useStripe();
  const elements = useElements();

  return <Review {...props} stripe={stripe} elements={elements} />;
}

export default ReviewWithStripe;